// ============================================
// APPLICATION - Session Hooks
// ============================================

import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../state/auth.store';
import { sessionRepository } from '../../infrastructure/http/session.repository';
import type { SessionData } from '../../domain/entities';

/**
 * Hook para obtener la sesión del usuario
 * Se ejecuta automáticamente después del login si hay token
 * Si el backend no responde pero hay datos en caché, usa el fallback
 */
export const useSession = (enabled: boolean = true) => {
  const { session: cachedSession, setSession, logout, accessToken } = useAuthStore();

  //console.log('[useSession] Hook config:', { enabled, hasAccessToken: !!accessToken, finalEnabled: enabled && !!accessToken });

  return useQuery<SessionData>({
    queryKey: ['session'],
    queryFn: async () => {
      //console.log('[useSession] QueryFn ejecutándose - Cargando datos de sesión...');
      try {
        const data = await sessionRepository.getSession();
        //console.log('[useSession] Sesión cargada exitosamente');
        setSession(data);
        return data;
      } catch (error: any) {
        // Si falla la autenticación (401, 403)
        const status = error?.response?.status;
        const isAuthError = status === 401 || status === 403;
        
        if (isAuthError) {
          logout();
          localStorage.removeItem('almacenes-digitales-cache');
          if ((window as any).electronAPI) {
            window.location.hash = '/auth/login';
          } else {
            window.location.href = '/auth/login';
          }
          throw error;
        }
        
        // Si es error de red/servidor Y tenemos sesión en caché, usar fallback
        if (cachedSession && !error?.response) {
          console.warn('Backend no disponible, usando sesión en caché');
          return cachedSession;
        }
        
        throw error;
      }
    },
    enabled: enabled && !!accessToken,
    staleTime: 60 * 60 * 1000, // 1 hora
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      // No reintentar si es error de autenticación
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      // Para errores de red, reintentar 2 veces
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
};
